import { ArrowUpOutlined, CloseOutlined } from "@ant-design/icons";
import { Image, message } from "antd";
import { chatApi } from "api/chat.api";
import { BButton, BInputChat } from "components/base";
import type { BInputChatAttachType } from "components/base/BInputChat/BInputChat";
import { observer } from "mobx-react";
import { useCallback, useRef, useState } from "react";
import { chatStore } from "store/chatStore";
import { userStore } from "store/userStore";
import type { ChatFile } from "types/chat.type";
import { showFirstAttachmentError } from "utils/attachmentSnackbar";
import {
  createChatFileFromIncoming,
  filesToImageCandidates,
  validateIncomingAttachments,
} from "utils/attachmentValidation";
import "./MessageComposer.scss";

type PreviewState = {
  open: boolean;
  image: string;
};

const MessageComposer = observer(function MessageComposer() {
  const { isAiResponding } = chatStore;
  const token = userStore.token;
  const [value, setValue] = useState("");
  const [attachments, setAttachments] = useState<ChatFile[]>([]);
  const [preview, setPreview] = useState<PreviewState>({ open: false, image: "" });
  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;

  const handleAttach = useCallback((files: File[], type: BInputChatAttachType) => {
    // Non-image path kept for later; ignore until kind rules are wired.
    if (type !== "image") {
      return;
    }

    const candidates = filesToImageCandidates(files);
    if (candidates.length === 0) {
      return;
    }

    const { accepted, errors } = validateIncomingAttachments(
      attachmentsRef.current,
      candidates,
    );

    if (errors.length > 0) {
      showFirstAttachmentError(errors);
    }

    if (accepted.length === 0) {
      return;
    }

    setAttachments((prev) => [...prev, ...accepted.map(createChatFileFromIncoming)]);
  }, []);

  const handleRemoveAttachment = useCallback((id: string) => {
    setAttachments((prev) => {
      const target = prev.find((file) => file.id === id);
      if (target?.url.startsWith("blob:")) {
        URL.revokeObjectURL(target.url);
      }
      return prev.filter((file) => file.id !== id);
    });
  }, []);

  const handleSubmit = useCallback(
    async (text: string) => {
      if (!token) {
        message.error("Vui lòng đăng nhập để gửi tin nhắn");
        return;
      }

      const content = text.trim();
      const pendingFiles = attachments;
      const hasFiles = pendingFiles.length > 0;

      if ((!content && !hasFiles) || chatStore.isAiResponding) {
        return;
      }

      setValue("");
      setAttachments([]);
      chatStore.setIsAiResponding(true);

      let typewriterPromise: Promise<void> | null = null;

      try {
        const uploadFiles = pendingFiles
          .map((item) => item.file)
          .filter((file): file is File => file instanceof File);

        const conversationId = await chatStore.ensureConversation();
        chatStore.addOptimisticUserMessage(content, hasFiles ? pendingFiles : undefined);
        chatStore.startStreamingAssistant();

        await chatApi.stream(
          {
            conversationId,
            content,
            files: uploadFiles.length > 0 ? uploadFiles : undefined,
          },
          (event) => {
            if (event.type === "token") {
              chatStore.enqueueStreamingToken(String(event.data ?? ""));
            }
            if (event.type === "title") {
              const payload = event.data as {
                conversationId?: number;
                name?: string;
              } | null;
              if (payload?.conversationId != null && payload.name) {
                chatStore.updateConversationName(payload.conversationId, payload.name);
              }
            }
            if (event.type === "done") {
              const payload = event.data as { messageId?: number } | null;
              typewriterPromise = chatStore.completeStreaming(
                payload?.messageId ?? Date.now(),
              );
            }
          },
        );

        if (typewriterPromise) {
          await typewriterPromise;
        } else if (chatStore.streamingMessageId != null) {
          const streaming = chatStore.messages.find(
            (msg) => msg.id === chatStore.streamingMessageId,
          );
          if (streaming?.content || chatStore.hasPendingStreamingTokens) {
            await chatStore.completeStreaming(Math.abs(chatStore.streamingMessageId));
          } else {
            chatStore.removeStreamingAssistant();
          }
        }
      } catch (error: unknown) {
        chatStore.removeStreamingAssistant();
        const err = error as { message?: string };
        message.error(err?.message || "Gửi tin nhắn thất bại");
        // Keep blob URLs on the optimistic message if already added; do not revoke.
      } finally {
        chatStore.setIsAiResponding(false);
      }
    },
    [token, attachments],
  );

  const handlePreview = useCallback((url: string) => {
    setPreview({ open: true, image: url });
  }, []);

  const canSend = Boolean(value.trim() || attachments.length > 0);

  const attachmentSlot =
    attachments.length > 0 ? (
      <>
        {attachments.map((file) => (
          <div key={file.id} className="message-composer__attachment">
            <button
              type="button"
              className="message-composer__attachment-preview"
              onClick={() => handlePreview(file.url)}
              aria-label={`Xem ${file.name}`}
            >
              <img
                className="message-composer__attachment-image"
                src={file.url}
                alt={file.name}
              />
            </button>
            <button
              type="button"
              className="message-composer__attachment-remove"
              onClick={() => handleRemoveAttachment(file.id)}
              aria-label={`Xóa ${file.name}`}
            >
              <CloseOutlined />
            </button>
          </div>
        ))}
      </>
    ) : null;

  return (
    <div className="message-composer">
      <div className="message-composer__control">
        <BInputChat
          className="message-composer__input"
          value={value}
          onChange={setValue}
          onAttach={handleAttach}
          onSubmit={handleSubmit}
          attachments={attachmentSlot}
          placeholder="Hỏi bất cứ điều gì"
          showAttach
          showMic={false}
          disabled={isAiResponding}
        />
        <BButton
          type="primary"
          className="message-composer__send"
          aria-label="Gửi tin nhắn"
          disabled={isAiResponding || !canSend}
          icon={<ArrowUpOutlined />}
          onClick={() => void handleSubmit(value.trim())}
        />
      </div>

      <p className="message-composer__note">
        AI giúp phân tích dựa trên tài liệu có sẵn, những thông tin quan trọng vui lòng đối chiếu lại với tài liệu.
      </p>

      {preview.image ? (
        <Image
          style={{ display: "none" }}
          preview={{
            visible: preview.open,
            onVisibleChange: (visible) => setPreview((prev) => ({ ...prev, open: visible })),
            afterOpenChange: (visible) => {
              if (!visible) {
                setPreview({ open: false, image: "" });
              }
            },
          }}
          src={preview.image}
        />
      ) : null}
    </div>
  );
});

export default MessageComposer;
